import type { AssetSelection } from '../../../types/asset.types';

/** What the download rules read. The wire `Asset` and the grid's
 *  `ImageAssetType` both have it, so the workspace and the orders row share
 *  one set of rules (DV-2763). */
type ScopePhoto = {
  photoUUID: string;
  hidden?: boolean | null;
  staged?: boolean | null;
};

/** What a download modal opens with: what it sends, and the hidden photos it
 *  may add under "Include hidden photos?". Built from ONE pool, so the opt-in
 *  can never reach photos the selection was not drawn from (DV-2856 search). */
export interface DownloadScope {
  selection: AssetSelection;
  hiddenPhotoUUIDs: string[];
}

/**
 * "Download All": nothing is checked, so the bulk default is every VISIBLE
 * photo in the pool (product AC, DV-2221). The include-hidden opt-in is the
 * way in for the hidden ones. Marked `isDownloadAll` so one visible photo is
 * not mistaken for a deliberate single pick (DV-2763).
 */
export const downloadAllSelection = (pool: ScopePhoto[]): AssetSelection => ({
  photoUUIDs: pool
    .filter((photo) => !photo.hidden)
    .map((photo) => photo.photoUUID),
  isDownloadAll: true
});

/**
 * The hidden photos a download modal may offer under "Include hidden photos?".
 *
 * DV-2820: a customer's pool holds only their OWN hidden photos. A hidden
 * STAGED photo is internal unapproved WIP (an AI render parked hidden) and
 * must never be offered to them; the asset team sees it like any other.
 */
export const hiddenDownloadPool = (
  pool: ScopePhoto[],
  seesHiddenStaged: boolean
): string[] =>
  pool
    .filter((photo) => photo.hidden && (seesHiddenStaged || !photo.staged))
    .map((photo) => photo.photoUUID);
